import { AddType, BlurEvent, DataSource, DataSourceMode, Label, LabelVariant, LookupModelSearchEvent, LookupModelSelectionEvent, PrintableEvent, Switch, Table, TableAddRowResult, TableColumn, TableRow, TableRowDisplayEvent, TableRowMode, Textbox, ValidationResult } from "@mcleod/components";
import { Api, DisplayType, HorizontalAlignment, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutResponsibleHist } from "./autogen/AutogenLayoutResponsibleHist";

enum ResponsibleRole { Entry = "E", Dispatch = "D", Manual = "M", Commision_Covered = "C" }
enum ResponsibleRoleType { Entry = "E", Dispatch = "D" }

export class ResponsibleHist extends AutogenLayoutResponsibleHist {

    private _hierarchyLevels: Array<string>;
    private _parentHash: Map<string, string>;
    private _hierarchyDescriptions: Map<string, string>;
    private _commissionRoleType: string
    private _commissionsEnabled: boolean
    private _splitPercentChanged: boolean;
    private _orderIds: Array<string>;

    private populateOtherLevelValuesRef = (event: LookupModelSelectionEvent) => {
        this.textboxHierarchyLevel1OnLookupModelSelection(event);
    };

    override async onLoad() {
        const titleLabel = new Label({
            variant: LabelVariant.sectionHeading,
            id: "labelTableTitle",
            text: "Responsibility Users", rowBreak: true, marginTop: 12,  marginBottom: 4
        });

        const infoLabel = new Label({
            id: "labelInfoText",
            text: "No hierarchy is defined.",
            color: "error.light", fontBold: true, margin: 0, rowBreak: true, marginTop: 0,  marginBottom: 2
        });

        this.insert(titleLabel, 0);

        const response = await Api.search("lme/general/validate-hierarchy", { "operation": "Both" });
        const data = response?.data[0];
        if (data) {
            const hierarchyLevelExist: boolean = data.hierarchy_level_exists;
            if (!hierarchyLevelExist) {
                this.tableResponsibleHist.addType = AddType.NOT_ALLOWED;
                this.insert(infoLabel, 1);
            }
        }

        this.tableResponsibleHist.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const tableRow: TableRow = event.getTableRow();
            const modelRow: ModelRow = tableRow.data;
            if (tableRow.mode == TableRowMode.QUICK_ADD) {
                this.setDefaultRole(tableRow, modelRow);
                this.addHierarchyLevelFilters(tableRow);
                return;
            }
            this.addHierarchyLevelFilters(tableRow);

            const responsibleRole = modelRow.get("responsible_role", null);
            if (StringUtil.equalsIgnoreCase(responsibleRole, ResponsibleRole.Entry)) {
                tableRow.allowDelete = false;
            }

            if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                if (modelRow.isNull("commission_role_type")) {
                    modelRow.set("commission_role_type", ResponsibleRoleType.Entry);
                }
            }

            const isCommisionEligible = modelRow.get("commissions_eligible");
            const responsibleRoleType = modelRow.get("commission_role_type", null);
            const isAppending = modelRow._appending == true;
            const isManualResponsibleRole = StringUtil.equalsIgnoreCase(responsibleRole, ResponsibleRole.Manual);
            const isCommissionIneligible = isCommisionEligible == null || StringUtil.equalsIgnoreCase(isCommisionEligible, "N");
            const isDispatchRoleType = StringUtil.equalsIgnoreCase(responsibleRoleType, ResponsibleRoleType.Dispatch);
            const isDispatchRole = StringUtil.equalsIgnoreCase(responsibleRole, ResponsibleRole.Dispatch);
            const isCommissionCovered = StringUtil.equalsIgnoreCase(responsibleRole, ResponsibleRole.Commision_Covered);

            const isSplitPercentNotVisible =
                isAppending || (isManualResponsibleRole && isCommissionIneligible) || isDispatchRoleType || isDispatchRole || isCommissionCovered;

            tableRow.forEveryChildComponent((component) => {
                if (component instanceof Textbox && component.id === "textboxSplitPercent") {
                    component.setProps({
                        enabled: !isSplitPercentNotVisible,
                        visible: !isSplitPercentNotVisible
                    });
                }
            });

            const textboxOrderId = tableRow.findComponentById("textboxOrderId") as Textbox;
            if (textboxOrderId != null && this._orderIds) {
                textboxOrderId.items = this._orderIds;
                textboxOrderId.setSelectedItemFromValue(modelRow.get("order_id", ""));
            }

            if (!modelRow._appending) {
                this.setCommissionsDisplayOnlyInformation(tableRow);
                this.checkCommissionsTransferredAndDisable(tableRow);
            }
            tableRow.displayComponentValues();
        });
    }

    public async setup(parentResponsibleHistDataSource: DataSource) {
        this.useProvidedResponsibleHistDataSource(parentResponsibleHistDataSource);
        await this.getResponsibilityConfiguration();
    }

    public useProvidedResponsibleHistDataSource(sourceResponsibleHist: DataSource) {
        this.sourceResponsibleHist = sourceResponsibleHist;
        this.replaceMainDatasource(this.sourceResponsibleHist);
        this.displayData(null, this.mainDataSource.data, null);
    }

    public async getResponsibilityConfiguration(): Promise<void | Array<string>> {
        return Api.search("lme/general/responsibility-configuration").then(result => {
            const data = result?.data[0];
            this.levels = data["responsibility_levels"];
            if (this._hierarchyLevels != null) {
                const parentHash = data["responsibility_parent_hash"];
                this._parentHash = parentHash;
                this._hierarchyDescriptions = data["responsibility_hierarchy_descriptions"];
            }
            return this._hierarchyLevels;
        });
    }

    private addHierarchyLevelFilters(row: TableRow) {
        for (let i = 1; i <= 5; ++i) {
            const textbox: Textbox = row.findComponentById("textboxHierarchyLevel" + i) as Textbox;
            if (textbox != null) {
                if (row.data != null) {
                    const value: string = row.data.get(textbox.field);
                    let descr: string;
                    if (this._hierarchyDescriptions != null)
                        descr = this._hierarchyDescriptions[value];
                    else
                        descr = value;
                    row.data.setLookupModelData(textbox.field, {"id": value, "description": descr });
                    textbox.displayData(row.data, null, 0);
                }
                textbox.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                    event.filter.hierarchy_level = i;
                });
                if (i == 1)
                    textbox.addLookupModelSelectionListener(this.populateOtherLevelValuesRef);
            }
        }
    }

    private textboxHierarchyLevel1OnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!event.hasSelection()) return;
        const tableRow = TableRow.getContainingTableRow(event.target);
        const data: ModelRow = tableRow.data;
        this.populateOtherLevelValues(event.target, tableRow, data);
    }

    private populateOtherLevelValues(textbox: Textbox, row: TableRow, data: ModelRow<any>) {
        let lastValue = textbox.getFirstLookupModelData().get("id");
        for (let i = 2; i <= this._hierarchyLevels.length; ++i) {
            const otherTextbox: Textbox = row.findComponentById("textboxHierarchyLevel" + i) as Textbox;
            const newValue = this._parentHash[lastValue];
            const newDescription = this._hierarchyDescriptions[newValue];
            data.set(otherTextbox.field, newValue);
            data.setLookupModelData(otherTextbox.field, {"id": newValue, "description": newDescription });
            otherTextbox.displayData(data, null, 0);
            lastValue = newValue;
        }
    }

    private setDefaultRole(row: TableRow, modelRow: ModelRow): void {
        const role: Textbox = row.findComponentById("textboxResponsibleRole") as Textbox;
        if (role != null) {
            modelRow.set("responsible_role", ResponsibleRole.Manual);
            modelRow.set("commission_role_type", this.commissionRoleType || ResponsibleRoleType.Entry);
            const items = role.items;
            for (let i = 0; i < items.length; ++i) {
                if (items[i].value == ResponsibleRole.Manual) {
                    role.selectedItem = items[i];
                    break;
                }
            }
        }
    }

    public set levels(hierarchyLevels: string[]) {
        this._hierarchyLevels = hierarchyLevels;
        const tableResponsibleHist: Table = this.tableResponsibleHist;
        const columnLength = tableResponsibleHist.columns.length;
        const useLocationPrefix: boolean = hierarchyLevels.length > 0 && hierarchyLevels[0].startsWith("loc");
        const itemsToRemove = 5 - hierarchyLevels.length;

        for (let i = 0; i < itemsToRemove; ++i)
            tableResponsibleHist.removeColumn(columnLength - 1 - i);

        if (useLocationPrefix) {
            for (let i = 0; i < hierarchyLevels.length; ++i) {
                const column = tableResponsibleHist.getColumnByCaption("Level " + (i + 1)) as TableColumn;
                column.headingCell.caption = "Loc " + (i + 1);
            }
        }

        tableResponsibleHist.columns[0].headingCell.caption = "Role";
        tableResponsibleHist.resetColumnSorting();
    }

    public set parentHash(hash: Map<string, string>) {
        this._parentHash = hash;
    }

    public set hierarchyDescriptions(hash: Map<string, string>) {
        this._hierarchyDescriptions = hash;
    }

    public set commissionRoleType(commissionRoleType: string) {
        this._commissionRoleType = commissionRoleType;
    }

    public get commissionRoleType() :string {
        return this._commissionRoleType;
    }

    public set splitPercentChanged(splitPercentChanged: boolean) {
        this._splitPercentChanged = splitPercentChanged;
    }

    public get splitPercentChanged() :boolean {
        return this._splitPercentChanged;
    }

    public set commissionsEnabled(commissionsEnabled: boolean) {
        this._commissionsEnabled = commissionsEnabled;
    }

    public get commissionsEnabled() :boolean {
        return this._commissionsEnabled;
    }

    public set orderIds(orderIds: Array<string>) {
        this._orderIds = orderIds;
    }

    public async addBlankEntryRecordToTable(): Promise<TableRow> {
        if (this.getTableEntryRow() != null) {
            return;
        }
        let addRowResult: TableAddRowResult;
        const newRowData = await this.tableResponsibleHist._createNewRowData();
        if (newRowData != null) {
            newRowData.set("responsible_role", ResponsibleRole.Entry);
            addRowResult = this.tableResponsibleHist.addRow(newRowData, {mode: TableRowMode.ADD}, { display: true, addToData: true });
        }
        addRowResult?.row.editRow();
        return addRowResult?.row;
    }

    private getTableEntryRow(): TableRow {
        for (const row of this.tableResponsibleHist.rows) {
            if (row.data.get("responsible_role", null) === ResponsibleRole.Entry) {
                return row;
            }
        }
        return null;
    }

    textboxCommissionRoleTypeOnChange(event) {
        const switchCommissionsEligible = event.target.row.findComponentById("switchCommissionsEligible") as Switch;
        if (event.newValue == "Dispatch" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("textboxSplitPercent").visible = false;
            event.target.row.findComponentById("textboxSplitPercent").text = "";
        } else if (event.newValue != "Dispatch" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("textboxSplitPercent").visible = switchCommissionsEligible.checked;
            event.target.row.findComponentById("textboxSplitPercent").enabled = switchCommissionsEligible.checked;
        }
    }

    textboxNotificationOnPrintable(event: PrintableEvent) {
        const textbox = event.target as Textbox;
        textbox.marginLeft = "auto";
        if (this.splitPercentChanged) {
            textbox.color = "";
            textbox.tooltip = null;
        } else {
            textbox.color = event.printable ? "warning" : "";
            textbox.tooltip = event.printable ? "This responsibility code will receive a remainder of the defined split percentages as indicated." : null;
        }
    }

    notificationOnBlur(event: BlurEvent) {
        if (!event.changedWhileFocused) {
            this.splitPercentChanged = false;
            return;
        }

        const notificationTextbox: Textbox = event.target as Textbox;
        const value = notificationTextbox.text;
        if (!StringUtil.isEmptyString(value)) {
            const number = Number(value);
            if (!isNaN(number) && Number.isInteger(number)) {
                notificationTextbox.displayType = DisplayType.INTEGER;
                notificationTextbox.maxValue = 100;
                notificationTextbox.minValue = 0;
                notificationTextbox.addlValidationCallback = null;
                notificationTextbox.validate(true, true);
                const tableRow: TableRow = TableRow.getContainingTableRow(notificationTextbox);
                const row: ModelRow = tableRow?.data;
                if (row != null) {
                    row.set("commissions_split_percent", value);
                }
                this.splitPercentChanged = true;
            } else {
                notificationTextbox.displayType = DisplayType.STRING;
                notificationTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                    return this.splitPercentValidator(notificationTextbox, mode);
                };
                notificationTextbox.validate(true, true);
                this.splitPercentChanged = false;
            }
        }
    }

    private splitPercentValidator(notificationTextBox: Textbox, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        return {
            component: notificationTextBox,
            isValid: false,
            validationMessage: "Split percent value must be between 0 and 100."
        };
    }

    public async checkCommissionsTransferredAndDisable(row: TableRow): Promise<void | Array<string>> {
        if (!row.data._appending) {
            const transferred = row.data.get("has_been_transferred_to_commissions");
            if (transferred) {
                row.allowEdit = false;
                row.allowDelete = false;
                row.tooltip = "Record has been transferred and cannot be edited";
            }
        }
    }

    switchCommissionsEligibleOnClick(event) {
        if (StringUtil.equalsIgnoreCase(event.target.row?.data.get("commission_role_type", null), "D") ||
            StringUtil.equalsIgnoreCase(event.target.row?.data.get("responsible_role", null), "D") ||
            StringUtil.equalsIgnoreCase(event.target.row?.data.get("responsible_role", null), "C")) {
            return;
        }

        const switchCommissionsEligible = event.target as Switch;
        const tb = event.target.row.findComponentById("textboxSplitPercent");

        if (tb) {
            tb.visible = switchCommissionsEligible.checked;
            tb.enabled = switchCommissionsEligible.checked;
            if (!switchCommissionsEligible.checked) {
                tb.text = "";
            }
        }
    }

    setCommissionsDisplayOnlyInformation(row: TableRow) {
        const splitPercentDisplay = row.data.get("commissions_split_display", null);
        const hasSplitPercentVariance = row.data.get("has_split_percent_variance", false);
        const hasHierarchyVariance = row.data.get("has_hierarchy_variance", false);

        row.cells.forEach(cell => {
            for (const component of cell.components) {
                if (component instanceof Textbox && component.id === "textboxSplitPercent") {
                    if (hasSplitPercentVariance) {
                        row.allowDelete = false;
                        row.allowEdit = false;
                        row.tooltip = "Updates to this responsibility record must be made in the Movement screen due to varying split percentages";
                        component.setProps({align: HorizontalAlignment.RIGHT, text: "Multiple", visible: true, color: "warning"});
                    } else if (!StringUtil.isEmptyString(splitPercentDisplay)) {
                        component.setProps({enabled: false, visible: false});
                    }
                }

                if (component instanceof Textbox && component.id === "textboxHierarchyLevel1") {
                    if (hasHierarchyVariance) {
                        row.allowDelete = false;
                        row.allowEdit = false;
                        row.tooltip = "Updates to this responsibility record must be made in the Movement screen due to varying responsibility codes";
                        component.setProps({align: HorizontalAlignment.RIGHT, visible: true, color: "warning"});
                    }
                }

                if (component instanceof Textbox && component.id === "textboxNotification") {
                    if (!StringUtil.isEmptyString(splitPercentDisplay)) {
                        component.setProps({ align: HorizontalAlignment.RIGHT, text: splitPercentDisplay, visible: true, color: "warning"});
                        if ("---" == splitPercentDisplay) {
                            component.tooltip = null;
                        } else {
                            component.tooltip = "This responsibility code will receive a remainder of the defined split percentages as indicated.";
                        }
                    }
                }
            }
        });
    }
}
